import { CurrencyPipe, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import {
  LOCALE_KEY_FR_FR,
  LOCALE_KEY_NB_NO,
  LOCALE_KEY_DE_DE,
  LOCALE_KEY_ES_ES,
  LOCALE_KEY_IT_IT,
  LOCALE_KEY_EN_GB,
  LOCALE_KEY_ES_US,
  XHR_TIMEOUT,
} from '@constants/app.constants';
import { ApiRequestInterceptor } from '@interceptors/api-request.interceptor';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { ErrorHandlerService } from '@services/error-handler/error-handler.service';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import {
  defineLocale,
  frLocale,
  nbLocale,
  deLocale,
  esLocale,
  itLocale,
  enGbLocale,
  esUsLocale,
} from 'ngx-bootstrap/chronos';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { getRoutes } from './app/app-routing.routes';
import { AppComponent } from './app/app.component';
import { effects, metaReducers, reducers } from './app/core';
import { FeatureFlagService } from './app/services/feature-flag/feature-flag.service';
import { environment } from './environments/environment';
import localeEn from '@angular/common/locales/en';
import localeIt from '@angular/common/locales/it';
import localeEnGb from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeNb from '@angular/common/locales/nb';
import localeDe from '@angular/common/locales/de';
import localeEsUs from '@angular/common/locales/es-US';
// I think this is used to format some currency data
// for Euro to get a certain type of display
import localeEs from '@angular/common/locales/es';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { GoogleMapsModule } from '@angular/google-maps';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { GeocodeService } from '@services/geocode/geocode.service';
import { firstValueFrom, map, race, timer } from 'rxjs';
import { provideToastr } from 'ngx-toastr';
import { UiMessageComponent } from './app/ui/components/ui-message/ui-message.component';

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeNb);
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeIt);
registerLocaleData(localeEnGb);
registerLocaleData(localeEsUs);

defineLocale(LOCALE_KEY_FR_FR, frLocale);
defineLocale(LOCALE_KEY_NB_NO, nbLocale);
defineLocale(LOCALE_KEY_DE_DE, deLocale);
defineLocale(LOCALE_KEY_ES_ES, esLocale);
defineLocale(LOCALE_KEY_IT_IT, itLocale);
defineLocale(LOCALE_KEY_EN_GB, enGbLocale);
defineLocale(LOCALE_KEY_ES_US, esUsLocale);

bootstrapApplication(AppComponent, {
  providers: [
    GrowthBookService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [GrowthBookService, GeocodeService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideAnimations(),
    provideToastr({
      toastComponent: UiMessageComponent,
      timeOut: 10000,
      positionClass: 'toast-top-right',
      maxOpened: 4,
      autoDismiss: true,
    }),
    provideLottieOptions({ player: () => player }),
    provideRouter(
      getRoutes(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideStore(reducers, { metaReducers }),
    provideEffects(effects),
    provideRouterStore(),
    isDevMode() ? provideStoreDevtools({ connectInZone: true }) : [],
    importProvidersFrom(
      TypeaheadModule.forRoot(),
      ModalModule.forRoot(),
      ButtonsModule.forRoot(),
      AccordionModule.forRoot(),
      AlertModule.forRoot(),
      CollapseModule.forRoot(),
      BsDatepickerModule.forRoot(),
      TooltipModule.forRoot(),
      GoogleMapsModule,
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiRequestInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    BsModalService,
    CurrencyPipe,
  ],
});

FeatureFlagService.loadConfig().catch((err) => console.log(err));

export function initializeApp(
  gbs: GrowthBookService,
  geocodeService: GeocodeService,
) {
  return (): Promise<[void, boolean | undefined]> => {
    const maps = firstValueFrom(
      race(
        geocodeService.loadMaps(),
        timer(XHR_TIMEOUT).pipe(map(() => false)),
      ),
    );
    return Promise.all([gbs.loadFeatures(), maps]);
  };
}
